import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { ArrowRight, Sparkles, Code2, Brain, Zap } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// AJ Code Labs Logo Component
const LogoAJ = ({ size = 100 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2" width="96" height="96" rx="28" fill="#FAFAFA" />
    <g transform="translate(22.5,22.2) scale(0.55)">
      <path d="M8 84 L31 16 L54 84" stroke="#0A0A0B" strokeWidth="11" strokeLinecap="square" />
      <path d="M19 61 H43" stroke="#0A0A0B" strokeWidth="11" strokeLinecap="square" />
      <path d="M92 16 V63 C92 76 82 85 69 85 C61 85 55 82 51 77" stroke="#FF4D2D" strokeWidth="11" strokeLinecap="square" />
    </g>
  </svg>
);

// Animated counter component
const AnimatedCounter = ({ value, suffix = '', duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = parseInt(value);
      const incrementTime = (duration * 1000) / end;
      
      const timer = setInterval(() => {
        start += 1;
        setCount(start);
        if (start >= end) clearInterval(timer);
      }, incrementTime);
      
      return () => clearInterval(timer);
    }
  }, [isInView, value, duration]);
  
  return <span ref={ref}>{count}{suffix}</span>;
};

// Floating service card component
const ServiceCard = ({ icon: Icon, title, delay, color }) => (
  <motion.div
    initial={{ opacity: 0, y: 30, scale: 0.9 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ delay, duration: 0.6, ease: "easeOut" }}
    className="bg-[#18181B] border border-[#27272A] rounded-xl p-4 flex items-center gap-3 hover:border-[#FF4D2D]/30 transition-all cursor-default"
  >
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <span className="text-[#FAFAFA] text-sm font-medium">{title}</span>
  </motion.div>
);

const HeroSection = () => {
  const { t, language } = useLanguage();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToServices = () => {
    const element = document.querySelector('#services');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Text reveal animation variants
  const textRevealVariants = {
    hidden: { y: 100, opacity: 0 },
    visible: (i) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    })
  };

  const services = language === 'es' 
    ? [
        { icon: Code2, title: 'Desarrollo de Software', color: 'bg-[#FF4D2D]' },
        { icon: Brain, title: 'Inteligencia Artificial', color: 'bg-purple-600' },
        { icon: Zap, title: 'Digitalización', color: 'bg-blue-600' },
      ]
    : [
        { icon: Code2, title: 'Software Development', color: 'bg-[#FF4D2D]' },
        { icon: Brain, title: 'Artificial Intelligence', color: 'bg-purple-600' },
        { icon: Zap, title: 'Digitalization', color: 'bg-blue-600' },
      ];

  return (
    <section 
      ref={sectionRef}
      className="min-h-screen relative flex items-center bg-[#0A0A0B] overflow-hidden" 
      data-testid="hero-section"
    >
      {/* Background elements */}
      <div className="absolute inset-0">
        {/* Gradient orbs */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#FF4D2D] rounded-full blur-[200px] opacity-[0.07]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-purple-600 rounded-full blur-[180px] opacity-[0.05]" />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(rgba(255,77,45,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,77,45,0.3) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>
      
      <motion.div 
        style={{ scale, opacity }}
        className="container-custom relative z-10 pt-32 pb-20"
      >
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Badge with entrance animation */}
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="inline-flex items-center gap-2 bg-[#FF4D2D]/10 border border-[#FF4D2D]/20 px-4 py-2 rounded-full mb-8"
            >
              <Sparkles className="w-4 h-4 text-[#FF4D2D]" />
              <span className="label-tag text-[#FF4D2D]">
                {t('hero.badge')}
              </span>
            </motion.div>

            {/* Main Headline with dramatic reveal */}
            <div className="mb-2">
              <motion.h1 
                custom={0}
                initial="hidden"
                animate="visible"
                variants={textRevealVariants}
                className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold tracking-tight leading-[1.02] text-[#FAFAFA]"
              >
                {t('hero.title1')}
              </motion.h1>
            </div>
            <div className="mb-6">
              <motion.h1 
                custom={1}
                initial="hidden"
                animate="visible"
                variants={textRevealVariants}
                className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold tracking-tight leading-[1.02]"
              >
                <span className="text-[#FF4D2D]">{t('hero.title2')}</span>
              </motion.h1>
            </div>

            {/* Subheadline */}
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-lg sm:text-xl text-[#A1A1AA] leading-relaxed max-w-xl mb-10"
            >
              {t('hero.subtitle')}
            </motion.p>

            {/* CTA Buttons with stagger */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <motion.button
                onClick={scrollToContact}
                className="bg-[#FF4D2D] hover:bg-[#E03A1C] text-[#FAFAFA] px-8 py-4 rounded-full font-semibold inline-flex items-center justify-center gap-2 group transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                data-testid="hero-cta-primary"
              >
                {t('hero.ctaPrimary')}
                <motion.span
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <ArrowRight className="w-5 h-5" />
                </motion.span>
              </motion.button>
              <motion.button
                onClick={scrollToServices}
                className="border border-[#27272A] text-[#FAFAFA] px-8 py-4 rounded-full font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#18181B] hover:border-[#FF4D2D]/30 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                data-testid="hero-cta-secondary"
              >
                {t('hero.ctaSecondary')}
              </motion.button>
            </motion.div>

            {/* Animated Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.6 }}
              className="flex gap-8 sm:gap-12 mt-12 pt-12 border-t border-[#27272A]"
            >
              {[
                { value: '50', suffix: '+', label: t('hero.stat1') },
                { value: '99', suffix: '%', label: t('hero.stat2') },
                { value: '24', suffix: '/7', label: t('hero.stat3') },
              ].map((stat, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 + (index * 0.1) }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="text-3xl sm:text-4xl font-bold text-[#FAFAFA]">
                    <AnimatedCounter value={stat.value} suffix={stat.suffix} duration={2} />
                  </div>
                  <div className="text-sm text-[#A1A1AA] mt-1">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Content - Visual Elements */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block relative"
          >
            <div className="relative">
              {/* Main Logo with glow */}
              <motion.div
                className="flex justify-center mb-8"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              >
                <div className="relative">
                  <LogoAJ size={180} />
                  <div className="absolute inset-0 bg-[#FF4D2D]/20 rounded-[36px] blur-3xl -z-10 scale-125" />
                </div>
              </motion.div>

              {/* Service Cards */}
              <div className="space-y-4 max-w-sm mx-auto">
                {services.map((service, index) => (
                  <ServiceCard 
                    key={index}
                    icon={service.icon}
                    title={service.title}
                    color={service.color}
                    delay={0.6 + (index * 0.15)}
                  />
                ))}
              </div>

              {/* Code snippet card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1, duration: 0.6 }}
                className="mt-6 bg-[#18181B] border border-[#27272A] rounded-xl p-4 max-w-sm mx-auto"
              >
                {/* Terminal Header */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F56]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#27C93F]" />
                  <span className="ml-2 text-xs text-[#52525B] font-mono">terminal</span>
                </div>

                {/* Code Content */}
                <pre className="font-mono text-xs leading-relaxed">
                  <code>
                    <span className="text-[#52525B]">$</span>{' '}
                    <span className="text-[#A1A1AA]">npx create</span>{' '}
                    <span className="text-[#FF4D2D]">@ajcodelabs</span>
                    <span className="text-[#A1A1AA]">/app</span>
                    {'\n'}
                    <span className="text-[#27C93F]">✓</span>{' '}
                    <span className="text-[#A1A1AA]">{language === 'es' ? 'Proyecto iniciado' : 'Project initialized'}</span>
                    <span className="text-[#FF4D2D] animate-pulse">|</span>
                  </code>
                </pre>
              </motion.div>

              {/* Floating accent elements */}
              <motion.div
                className="absolute -top-4 -right-4 w-20 h-20 border border-[#FF4D2D]/20 rounded-full"
                animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 4, repeat: Infinity }}
              />
              <motion.div
                className="absolute bottom-20 -left-8 w-16 h-16 border border-purple-500/20 rounded-full"
                animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
                transition={{ duration: 5, repeat: Infinity, delay: 1 }}
              />
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Animated Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 15, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <motion.div 
          className="w-6 h-10 border border-[#27272A] rounded-full flex justify-center pt-2 cursor-pointer hover:border-[#FF4D2D]/50 transition-colors"
          onClick={scrollToServices}
          whileHover={{ scale: 1.1 }}
        >
          <motion.div 
            className="w-1.5 h-3 bg-[#FF4D2D] rounded-full"
            animate={{ y: [0, 8, 0], opacity: [1, 0.5, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
